import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { toast } from "sonner";
import { Share2, Lock, Globe, Copy, PencilLine, Eye } from "lucide-react";

export function FamilyShareDialog({ open, onOpenChange, family, isAdmin, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [joinEnabled, setJoinEnabled] = useState(!!family?.join_enabled);
  const [joinToken, setJoinToken] = useState(family?.join_token || null);

  useEffect(() => {
    setJoinEnabled(!!family?.join_enabled);
    setJoinToken(family?.join_token || null);
  }, [family, open]);

  const isPublic = !!family?.public;
  const viewLink = family?.slug ? `${window.location.origin}/family/${family.slug}` : "";
  const editLink = joinToken ? `${window.location.origin}/invite/${joinToken}` : "";

  const togglePublic = async (next) => {
    setBusy(true);
    try {
      await api.setFamilyPublic(next);
      toast.success(next ? "View-only link is on" : "Your tree is private again");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update sharing"); }
    finally { setBusy(false); }
  };

  const toggleEdit = async (next) => {
    setBusy(true);
    try {
      const res = await api.setJoinLink(next);
      setJoinEnabled(res.enabled);
      setJoinToken(res.token);
      toast.success(next ? "Anyone with this link can now join & edit" : "Edit link turned off");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the edit link"); }
    finally { setBusy(false); }
  };

  const copy = async (link, label) => {
    const ok = await copyText(link);
    ok ? toast.success(`${label} copied`) : toast.error("Could not copy the link");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="family-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]"><Share2 size={20} /> Share your family tree</DialogTitle>
          <DialogDescription>Your tree is private by default. Choose how you want to share it.</DialogDescription>
        </DialogHeader>

        {/* View-only public link */}
        <div className="rounded-2xl border border-[#D8E0E8] bg-[#F4F6F8] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${isPublic ? "bg-[#007F6B]/12 text-[#007F6B]" : "bg-[#E8EEF3] text-[#082D4A]"}`}>
                {isPublic ? <Globe size={18} /> : <Eye size={18} />}
              </span>
              <div>
                <div className="text-sm font-semibold text-[#082D4A]">View-only link</div>
                <div className="text-xs text-[#64748B]">Anyone with the link can look, but not change anything</div>
              </div>
            </div>
            {isAdmin ? (
              <Switch checked={isPublic} disabled={busy} onCheckedChange={togglePublic} data-testid="family-public-toggle" />
            ) : <span className="text-xs text-[#94A3B8]">Admin only</span>}
          </div>
          {isPublic && viewLink && (
            <div className="mt-4 flex gap-2">
              <Input readOnly value={viewLink} data-testid="family-public-link" className="h-10 bg-white text-xs" onFocus={(e) => e.target.select()} />
              <Button onClick={() => copy(viewLink, "View link")} data-testid="family-copy-link" className="h-10 shrink-0 rounded-lg bg-[#082D4A] text-white hover:bg-[#08223A]"><Copy size={15} /></Button>
            </div>
          )}
        </div>

        {/* Editor join link */}
        <div className="rounded-2xl border border-[#D4A017]/30 bg-[#D4A017]/5 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${joinEnabled ? "bg-[#D4A017]/20 text-[#B0850F]" : "bg-[#E8EEF3] text-[#082D4A]"}`}>
                <PencilLine size={18} />
              </span>
              <div>
                <div className="text-sm font-semibold text-[#082D4A]">Invite people to edit</div>
                <div className="text-xs text-[#64748B]">They sign up once, then can add & edit any part of the tree</div>
              </div>
            </div>
            {isAdmin ? (
              <Switch checked={joinEnabled} disabled={busy} onCheckedChange={toggleEdit} data-testid="family-edit-toggle" />
            ) : <span className="text-xs text-[#94A3B8]">Admin only</span>}
          </div>
          {joinEnabled && editLink && (
            <div className="mt-4 flex gap-2">
              <Input readOnly value={editLink} data-testid="family-edit-link" className="h-10 bg-white text-xs" onFocus={(e) => e.target.select()} />
              <Button onClick={() => copy(editLink, "Edit link")} data-testid="family-copy-edit-link" className="h-10 shrink-0 rounded-lg bg-[#D4A017] text-white hover:bg-[#B0850F]"><Copy size={15} /></Button>
            </div>
          )}
          {joinEnabled && (
            <p className="mt-2 flex items-center gap-1 text-xs text-[#64748B]"><Lock size={11} /> Turn this off any time to stop new sign-ups. Admins can always fix mistakes.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
